// Upload .xlsx, preview rows, validate basic required fields, then POST rows to /api/members/import

import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * âœ… UPDATED: Added monthlyShareCommitment to template headers
 */
const TEMPLATE_HEADERS = [
  'memberId','fullName','idNumber','gender','dateOfBirth',
  'phoneNumber','email','address','city','homeCounty','dateJoined',
  'employer','department','payrollNumber',
  'kinName','kinRelationship','kinPhone','kinBenefitPercentage',
  'otherKinNames','otherKinRelationships','otherKinPercentages'
];

export default function MembersImportPage() {
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  function downloadTemplate() {
    const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'MembersTemplate');
    XLSX.writeFile(wb, 'members_import_template.xlsx');
  }

  function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: 'binary' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { defval: '' });

      // Normalize column names to expected headers
      const normalized = data.map((r) => {
        const out = {};
        Object.keys(r).forEach((k) => { out[String(k).trim()] = r[k]; });
        // fill missing headers
        TEMPLATE_HEADERS.forEach(h => { if (!(h in out)) out[h] = ''; });
        return out;
      });

      setRows(normalized);
      setErrors([]);
      setResult(null);
    };
    reader.readAsBinaryString(file);
  }

  function quickValidate(r, idx) {
    const errs = [];
    if (!String(r.fullName || '').trim()) errs.push('fullName missing');
    if (!String(r.phoneNumber || '').trim()) errs.push('phoneNumber missing');
    if (!String(r.dateJoined || '').trim()) errs.push('dateJoined missing');
    const dj = new Date(r.dateJoined);
    if (String(r.dateJoined).trim() && isNaN(dj.getTime())) errs.push('dateJoined invalid');
    if (String(r.dateOfBirth).trim()) {
      const dob = new Date(r.dateOfBirth);
      if (isNaN(dob.getTime())) errs.push('dateOfBirth invalid');
    }
    return errs.length ? `Row ${idx + 2}: ${errs.join(', ')}` : null; // +2 accounting for header + 1-index
  }

  async function doImport() {
    setBusy(true);
    setErrors([]);
    setResult(null);
    try {
      // Client-side sanity
      const errs = rows
        .map((r, i) => quickValidate(r, i))
        .filter(Boolean);
      if (errs.length) {
        setErrors(errs);
        setBusy(false);
        return;
      }

      const token = localStorage.getItem('hopeSaccoToken');
      const res = await fetch(`${API_URL}/members/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ rows })
      });
      
      const j = await res.json();
      
      // Enhanced error handling - show exact error from backend
      if (!res.ok) {
        const errorMessages = [];
        errorMessages.push(`HTTP ${res.status}: ${res.statusText}`);
        if (j.message) errorMessages.push(`Message: ${j.message}`);
        if (j.error) errorMessages.push(`Error: ${j.error}`);
        if (j.details) errorMessages.push(`Details: ${j.details}`);
        throw new Error(errorMessages.join(' | '));
      }
      
      if (!j.success) throw new Error(j.message || j.error || 'Import failed');

      setResult(j.data);
    } catch (err) {
      setErrors([err.message || 'Import failed']);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminLayout pageTitle="Members Import (Excel)">
      <div className="card">
        <div className="card-body">
          <p>
            Download the template, fill it, then upload the file. Columns must match headers exactly.
          </p>
          
          <div className="info-box" style={{ marginBottom: '20px' }}>
            <span className="info-icon">â„¹ï¸</span>
            <div>
              <p><strong>Template includes the following fields:</strong></p>
              <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
                <li><strong>memberId</strong> - Optional, auto-generated if blank (e.g., S00123)</li>
                <li><strong>fullName</strong> - Required</li>
                <li><strong>phoneNumber</strong> - Required</li>
                <li><strong>dateJoined</strong> - Required (format: YYYY-MM-DD)</li>
                <li><strong>kinBenefitPercentage</strong> - Primary NOK benefit % (e.g. 100)</li>
                <li><strong>homeCounty</strong> - Member's home county (e.g. Nakuru)</li>
                <li><strong>otherKinNames</strong> - Comma-separated additional NOK names</li>
                <li><strong>otherKinRelationships</strong> - Comma-separated relationships</li>
                <li><strong>otherKinPercentages</strong> - Comma-separated benefit percentages</li>
              </ul>
            </div>
          </div>

          <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:12 }}>
            <button onClick={downloadTemplate}>Download Template</button>
            <input type="file" accept=".xlsx,.xls" onChange={handleFile} />
            <button onClick={doImport} disabled={!rows.length || busy}>
              {busy ? 'Importingâ€¦' : 'Import'}
            </button>
          </div>

          {errors.length > 0 && (
            <div className="alert alert-danger">
              <b>âš ï¸ Import Issues:</b>
              <ul>{errors.map((e,i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}

          {rows.length > 0 && (
            <>
              <h4>Preview (first 10 rows)</h4>
              <div style={{ overflowX:'auto' }}>
                <table className="table table-sm">
                  <thead>
                    <tr>{TEMPLATE_HEADERS.map(h => <th key={h}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {rows.slice(0,10).map((r, i) => (
                      <tr key={i}>
                        {TEMPLATE_HEADERS.map(h => <td key={h}>{String(r[h] ?? '')}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <small>Total rows: {rows.length}</small>
            </>
          )}

          {result && (
            <div className="alert alert-success" style={{ marginTop:12 }}>
              <div><b>âœ… Imported:</b> {result.imported}</div>
              <div><b>â­ï¸ Skipped:</b> {result.skipped}</div>
              {result.results?.length ? (
                <details style={{ marginTop:8 }}>
                  <summary>Details</summary>
                  <pre style={{ whiteSpace:'pre-wrap' }}>
{JSON.stringify(result.results, null, 2)}
                  </pre>
                </details>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
